import { useState } from "react";
import { Loader2, Gift, CalendarDays, Send } from "lucide-react";
import { toast } from "sonner";
import { api, gbp, errMsg } from "@/lib/api";

const AMOUNTS = [10, 25, 50, 100];
const INPUT = "mt-1.5 w-full rounded-xl border border-[#E2D7C3] bg-white px-4 py-3 outline-none focus:ring-2 focus:ring-[#8C2220]/30";
const iso = (d) => d.toLocaleDateString("en-CA");

function Field({ label, testid, ...props }) {
  return <label className="text-sm"><span className="text-slate-600">{label}</span><input {...props} className={INPUT} data-testid={testid} /></label>;
}

function CardPreview({ amount, to, from, message }) {
  return (
    <div className="relative rounded-3xl bg-[#1E2A44] text-[#F7F3EB] p-8 shadow-2xl overflow-hidden aspect-[1.6/1] flex flex-col justify-between" data-testid="giftcard-preview">
      <div className="absolute -right-10 -top-10 w-48 h-48 rounded-full border-[18px] border-[#D4AF37]/20" />
      <div className="flex items-center gap-3"><img src="/logo-mark.png" alt="" className="w-10 h-10" /><span className="font-mono text-xs tracking-[0.3em] text-[#D4AF37]">GIFT CARD</span></div>
      <div>
        <p className="font-serif text-5xl font-bold" data-testid="giftcard-preview-amount">{gbp(amount || 0)}</p>
        <p className="mt-2 text-sm text-slate-300">For {to || "someone special"}{from && ` · from ${from}`}</p>
        {message && <p className="mt-2 font-serif italic text-sm text-slate-200 line-clamp-2">“{message}”</p>}
      </div>
    </div>
  );
}

function BalanceCheck() {
  const [code, setCode] = useState("");
  const [bal, setBal] = useState(null);
  const check = async () => {
    try { const { data } = await api.post("/giftcards/check", { code }); setBal(data.balance); } catch (e2) { setBal(null); toast.error(errMsg(e2)); }
  };
  return (
    <div className="mt-8 rounded-2xl border border-[#EDE4D3] bg-white p-5" data-testid="giftcard-balance-form">
      <p className="font-semibold">Check a gift card balance</p>
      <div className="mt-3 flex gap-2">
        <input value={code} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (code.length >= 4) check(); } }} onChange={(e) => { setCode(e.target.value.toUpperCase()); setBal(null); }} placeholder="ALBN-XXXX-XXXX" className="flex-1 rounded-full border border-[#E2D7C3] px-4 py-2 text-sm font-mono" data-testid="giftcard-balance-input" />
        <button type="button" onClick={check} disabled={code.length < 4} className="rounded-full border border-[#1B3626] px-4 py-2 text-sm font-semibold disabled:opacity-40" data-testid="giftcard-balance-button">Check</button>
      </div>
      {bal != null && <p className="mt-3 text-sm" data-testid="giftcard-balance-result">Balance remaining: <b className="text-emerald-700">{gbp(bal)}</b></p>}
    </div>
  );
}

export default function GiftCards() {
  const [amount, setAmount] = useState(25);
  const [custom, setCustom] = useState("");
  const [f, setF] = useState({ to_name: "", to_email: "", message: "", from_name: "", from_email: "" });
  const [later, setLater] = useState(false);
  const [date, setDate] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const value = custom !== "" ? Number(custom) : amount;
  const tomorrow = new Date(Date.now() + 864e5);
  const maxDate = new Date(Date.now() + 365 * 864e5);

  const submit = async (e) => {
    e.preventDefault();
    if (!(value >= 5 && value <= 250)) return toast.error("Choose an amount between £5 and £250");
    if (later && !date) return toast.error("Pick the date to send the gift card");
    setBusy(true);
    try {
      const { data } = await api.post("/giftcards/checkout", {
        amount: value, message: f.message, send_date: later ? date : null, origin_url: window.location.origin,
        buyer: { name: f.from_name, email: f.from_email }, recipient: { name: f.to_name, email: f.to_email },
      });
      window.location.href = data.checkout_url;
    } catch (e2) { toast.error(errMsg(e2)); setBusy(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="giftcards-page">
      <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#8C2220]">E-gift cards</p>
      <h1 className="mt-3 font-serif text-4xl sm:text-5xl font-bold max-w-2xl leading-tight">Give the gift of a well-stocked pantry</h1>
      <p className="mt-4 text-slate-600 max-w-xl">Emailed straight away or on the day you choose — perfect for birthdays and Christmas. Spend it across as many orders as they like.</p>
      <form onSubmit={submit} className="mt-10 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7 space-y-8">
          <section>
            <h2 className="font-semibold text-lg">1. Choose an amount</h2>
            <div className="mt-4 flex flex-wrap gap-3">
              {AMOUNTS.map((a) => (
                <button type="button" key={a} onClick={() => { setAmount(a); setCustom(""); }} className={`rounded-2xl border-2 px-6 py-4 font-serif text-2xl font-bold transition-colors ${custom === "" && amount === a ? "border-[#8C2220] bg-[#8C2220]/5 text-[#8C2220]" : "border-[#E2D7C3] bg-white hover:border-slate-400"}`} data-testid={`giftcard-amount-${a}`}>£{a}</button>
              ))}
              <label className={`rounded-2xl border-2 px-4 py-2 flex items-center gap-1 bg-white ${custom !== "" ? "border-[#8C2220]" : "border-[#E2D7C3]"}`}>
                <span className="font-serif text-2xl font-bold">£</span>
                <input type="number" min={5} max={250} step="1" value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Other" className="w-24 outline-none font-serif text-2xl font-bold bg-transparent" data-testid="giftcard-custom-amount" />
              </label>
            </div>
            <p className={`mt-2 text-xs ${custom !== "" && !(value >= 5 && value <= 250) ? "text-rose-700 font-semibold" : "text-slate-500"}`} data-testid="giftcard-amount-hint">Custom amounts from £5 to £250.</p>
          </section>
          <section>
            <h2 className="font-semibold text-lg">2. Who is it for?</h2>
            <div className="mt-4 grid sm:grid-cols-2 gap-4">
              <Field label="Their name" required value={f.to_name} onChange={set("to_name")} testid="giftcard-to-name" />
              <Field label="Their email" type="email" required value={f.to_email} onChange={set("to_email")} testid="giftcard-to-email" />
              <label className="text-sm sm:col-span-2"><span className="text-slate-600">Personal message (optional)</span>
                <textarea rows={3} maxLength={300} value={f.message} onChange={set("message")} placeholder="Happy birthday! Treat yourself to something lovely." className={`${INPUT} font-serif italic`} data-testid="giftcard-message" /></label>
            </div>
          </section>
          <section>
            <h2 className="font-semibold text-lg">3. When should it arrive?</h2>
            <div className="mt-4 grid sm:grid-cols-2 gap-4">
              <button type="button" onClick={() => setLater(false)} className={`text-left rounded-2xl border-2 p-4 ${!later ? "border-[#8C2220] bg-[#8C2220]/5" : "border-[#E2D7C3] bg-white"}`} data-testid="giftcard-send-now"><Send className="w-5 h-5 text-[#8C2220]" /><p className="mt-2 font-semibold">Send straight away</p><p className="text-xs text-slate-500">As soon as payment is complete</p></button>
              <button type="button" onClick={() => setLater(true)} className={`text-left rounded-2xl border-2 p-4 ${later ? "border-[#8C2220] bg-[#8C2220]/5" : "border-[#E2D7C3] bg-white"}`} data-testid="giftcard-send-later"><CalendarDays className="w-5 h-5 text-[#8C2220]" /><p className="mt-2 font-semibold">On a date I choose</p><p className="text-xs text-slate-500">Arrives around 8am UK time</p></button>
            </div>
            {later && <input type="date" min={iso(tomorrow)} max={iso(maxDate)} value={date} onChange={(e) => setDate(e.target.value)} className="mt-4 rounded-xl border border-[#E2D7C3] bg-white px-4 py-3" data-testid="giftcard-send-date" />}
          </section>
          <section>
            <h2 className="font-semibold text-lg">4. Your details</h2>
            <div className="mt-4 grid sm:grid-cols-2 gap-4">
              <Field label="Your name" required value={f.from_name} onChange={set("from_name")} testid="giftcard-from-name" />
              <Field label="Your email (for the receipt)" type="email" required value={f.from_email} onChange={set("from_email")} testid="giftcard-from-email" />
            </div>
          </section>
        </div>
        <aside className="lg:col-span-5">
          <div className="lg:sticky lg:top-32">
            <CardPreview amount={Math.min(Math.max(value || 0, 0), 250)} to={f.to_name} from={f.from_name} message={f.message} />
            <button disabled={busy || !(value >= 5 && value <= 250)} className="mt-6 w-full rounded-full bg-[#8C2220] text-white py-4 font-semibold flex items-center justify-center gap-2 hover:bg-[#701A18] disabled:opacity-60 transition-colors" data-testid="giftcard-pay-button">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />} Buy gift card {gbp(value || 0)}
            </button>
            <p className="mt-3 text-xs text-slate-500 text-center">Secure payment by card. Gift cards can't be exchanged for cash.</p>
            <BalanceCheck />
          </div>
        </aside>
      </form>
    </div>
  );
}
